/*
 * <slider-banners>
 * Wraps a <carousel-slider> (which handles swiping, arrows and autoplay) and adds:
 *   - the "current / total" counter
 *   - the play/pause toggle (`[data-slider-banners-toggle]`) for autoplay
 *   - playing only the visible videos of the active slide
 *   - `data-auto-height`: slides keep their own media aspect ratio and the track follows the
 *     height of the active slide (without JS the tallest slide sets the track height)
 *   - `data-full-screen`: measures the header group (announcement bar + header) into
 *     `--header-group-height` on <html>, so full screen slides fit below it
 */
if (!customElements.get("slider-banners")) {
  class SliderBanners extends HTMLElement {
    connectedCallback() {
      this.carousel = this.querySelector("carousel-slider");
      if (!this.carousel) return;

      this.track = this.querySelector("[data-carousel-track]");
      this.current = this.querySelector("[data-slider-banners-current]");
      this.toggle = this.querySelector("[data-slider-banners-toggle]");
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener(
        "carousel:change",
        (event) => this.onChange(event.detail.page),
        { signal },
      );

      // Autoplay never runs with reduced motion, so the toggle would do nothing
      if (this.toggle && this.reducedMotion.matches) this.toggle.hidden = true;
      this.toggle?.addEventListener("click", () => this.togglePlayback(), { signal });

      if (this.hasAttribute("data-full-screen")) {
        this.measureHeaderGroup();
        window.addEventListener("resize", () => this.measureHeaderGroup(), { signal });
      }

      if (this.hasAttribute("data-auto-height")) {
        this.resizeObserver = new ResizeObserver(() => this.syncHeight());
        this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      }

      // The carousel may have reported its first page before this element was upgraded
      this.onChange(Math.max(this.carousel.currentPage ?? 0, 0));
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
    }

    get slides() {
      return [...this.querySelectorAll(".slider-banners__slide")];
    }

    onChange(page) {
      this.page = page;
      if (this.current) this.current.textContent = page + 1;
      this.syncVideos(page);
      if (this.resizeObserver) this.syncHeight();
    }

    /** Auto height: sizes the track to the active slide. */
    syncHeight() {
      const slide = this.slides[this.page];
      if (slide) this.track.style.height = `${slide.offsetHeight}px`;
    }

    /** Plays the visible videos of the active slide and pauses the rest. */
    syncVideos(activeIndex) {
      this.slides.forEach((slide, index) => {
        slide.querySelectorAll("video").forEach((video) => {
          const visible = video.checkVisibility
            ? video.checkVisibility()
            : video.offsetParent !== null;

          if (index === activeIndex && visible) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      });
    }

    togglePlayback() {
      const paused = !this.toggle.hasAttribute("data-paused");

      this.toggle.toggleAttribute("data-paused", paused);
      this.toggle.setAttribute(
        "aria-label",
        paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause,
      );

      if (paused) {
        this.carousel.pauseAutoplay("user");
      } else {
        this.carousel.resumeAutoplay("user");
      }
    }

    measureHeaderGroup() {
      const height = [
        ...document.querySelectorAll(".shopify-section-group-header-group"),
      ].reduce((total, section) => total + section.offsetHeight, 0);

      document.documentElement.style.setProperty("--header-group-height", `${height}px`);
    }
  }

  customElements.define("slider-banners", SliderBanners);
}
